import React, { useContext } from 'react';
import {
  Grid,
  LinearProgress,
  CardContent,
  Typography,
  Card,
  Button,
  Box,
} from '@material-ui/core';
import { useIsFetching } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import Calendar from 'components/Calendar/Calendar';
import JobsByStatus from 'components/Jobs/Graphs/JobsByStatusChart';
import TopClientsByVolumeChart from 'components/Jobs/Graphs/JobsByClientChart';
import JobsByCoordinatorChart from 'components/Jobs/Graphs/JobsByCoordinatorChart';
import JobHealthByCoordinatorChart from 'components/Jobs/Graphs/JobHealthByCoordinatorChart';
import NeedsAttentionCard from 'components/Jobs/Graphs/NeedsAttentionCard';
import JobStatusByClientChart from 'components/Jobs/Graphs/JobStatusByClientChart';
import UserContext from 'state/UserContext';
import { userHasOverride } from 'models/Role';
import { UserFeatureOverride } from 'api/userApi';
import type { DashboardView } from 'views/Dashboard/DashboardTabs';

interface Props {
  /** Active dashboard tab. Only the active tab mounts. */
  view: DashboardView;
}

const PMGridItems = ({ view }: Props) => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const isFetchingCalendar = useIsFetching(['calendar-events', 'today']);
  // Narrow, per-widget exception (see UserFeatureOverride) — does not touch which
  // grid (Admin vs PM) mounts, so no other admin-only widget becomes visible.
  const showOrgJobsByStatus = userHasOverride(
    user,
    UserFeatureOverride.ORG_JOBS_BY_STATUS_VIEW
  );
  const showOrgNeedsAttention = userHasOverride(
    user,
    UserFeatureOverride.ORG_NEEDS_ATTENTION_VIEW
  );

  const TodayTasksCard = (
    <Grid item xs={12} md={12} lg={3}>
      <Card>
        {isFetchingCalendar > 0 && <LinearProgress />}
        <CardContent>
          <Box mb={2}>
            <Typography variant="overline">Your day at a glance</Typography>
            <Typography variant="h4" gutterBottom>
              Scheduled tasks for Today
            </Typography>
          </Box>
          <Calendar
            showToolbar={false}
            defaultView={'day'}
            step={30}
          />
          <Button
            variant="outlined"
            style={{ marginTop: '2rem' }}
            size="small"
            color="primary"
            onClick={() => navigate('/calendar')}>
            Open calendar
          </Button>
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <>
      {view === 'overview' && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={4}>
            {showOrgJobsByStatus ? (
              <JobsByStatus />
            ) : (
              <JobsByStatus label="Your Jobs by Status" coordinatorId={user?.id} />
            )}
          </Grid>
          <Grid item xs={12} md={6} lg={5}>
            {showOrgNeedsAttention ? (
              <NeedsAttentionCard />
            ) : (
              <NeedsAttentionCard coordinatorId={user?.id} />
            )}
          </Grid>
          {TodayTasksCard}
        </Grid>
      )}
      {view === 'workload' && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={12} lg={6}>
            <JobsByCoordinatorChart />
          </Grid>
          <Grid item xs={12} md={12} lg={6}>
            <JobHealthByCoordinatorChart />
          </Grid>
        </Grid>
      )}
      {view === 'clients' && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={12} lg={6}>
            <TopClientsByVolumeChart
              label="Your Top Clients by Volume"
              coordinatorId={user?.id}
            />
          </Grid>
          <Grid item xs={12} md={12} lg={6}>
            <JobStatusByClientChart
              label="Your Job Status by Client"
              coordinatorId={user?.id}
            />
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default PMGridItems;
